import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useState } from 'react';
import { Container } from 'components/Container';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { RescheduleMixpanelEvents } from 'domains/direct-reschedule/mixpanel';
import { Heading } from 'components/Typography';
import { useNavigation } from 'utils/hooks/useNavigation';
import { Button, IconStartAdornment } from 'components/Button';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import { isTimeSlotAvailable, useRescheduleState } from 'domains/direct-reschedule/useRescheduleProvider';
import { ReschedulePendingRescheduleDto, reschedulePendingReschedule } from 'domains/direct-reschedule/queries';
import { ApiError } from 'domains/api/errors';
import { useFlashMessages } from 'domains/flash-messages';
import { useModal } from 'utils/hooks/useModal';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { RescheduleConfirmationModal, RescheduleConfirmationResponse } from './RescheduleConfirmationModal';
import { AppointmentDate } from 'components/AppointmentDate';

const RescheduleConfirmation = () => {
  useTrack(RescheduleMixpanelEvents.ViewedRescheduleConfirmation);

  const { openModal, modalProps } = useModal({ dismissable: false });
  const { timeSlot, clientId, rescheduleId } = useRescheduleState();
  const queryClient = useQueryClient();

  const { displayTimezone } = useDisplayTimezone();
  const { routes, navigate: sendTo } = useNavigation();

  const [modalStatus, setModalStatus] = useState<RescheduleConfirmationResponse | null>(null);

  const { t } = useTranslation();
  const { createFlash } = useFlashMessages();
  const navigate = useNavigate();

  const isMobile = useBreakpoint({
    max: 'xs',
  });

  const { mutateAsync: rescheduleAppointment, isLoading: isRescheduling } = useMutation<
    unknown,
    ApiError,
    ReschedulePendingRescheduleDto
  >(
    data => {
      if (!clientId || !rescheduleId) throw new Error('clientId or rescheduleId is undefined');
      const response = reschedulePendingReschedule(clientId, rescheduleId, data);

      return response;
    },
    {
      onSuccess: async () => {
        setModalStatus('success');
        openModal();
      },
      onError: async error => {
        // @ts-expect-error @TODO proper types here
        const code = error?.originalError?.response?.data?.code;

        switch (code) {
          case 'PENDING_RESCHEDULE_NOT_ACTIVE':
          case 'ACUITY_CALENDAR_NOT_AVAILABILITY':
            await queryClient.invalidateQueries('reschedule-available-times');
            setModalStatus('inactive');
            break;
          case 'PENDING_RESCHEDULE_NOT_FOUND':
            setModalStatus('notfound');
            break;
          default:
            // dev-note(gian): this is a catch-all error message for any other error that we don't know about
            sendTo(routes.root, {
              flash: createFlash('error', t('reschedule.confirm.genericError')),
            });
            return;
        }

        openModal();
      },
    },
  );

  const onConfirm = useCallback(() => {
    if (modalStatus !== 'success') navigate(-1);
    else sendTo(routes.root);
  }, [modalStatus]);

  if (!timeSlot || !displayTimezone) {
    sendTo(`/reschedule/${clientId}/${rescheduleId}`, {
      flash: createFlash('error', t('reschedule.confirm.invalidRescheduleState')),
    });
    return null;
  }

  const onSubmit = () => {
    // edge-case: if the user has been on the page for more than 2 hours from the selected timeslot, we don't allow them to reschedule
    if (!isTimeSlotAvailable(timeSlot)) {
      setModalStatus('inactive');
      openModal();

      return;
    }

    rescheduleAppointment({ dateTime: timeSlot.toISOString(), timeZone: displayTimezone?.resolvedName ?? '' });
  };

  return (
    <>
      <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
      <Container className="bg-cream-50  max-w-[55rem] in-h-full flex flex-col items-center" size="full">
        <div className="flex-col mb-40 md:mb-64 items-start">
          <Button
            onClick={() => navigate(-1)}
            className="-ml-[24px] mb-[24px]"
            startAdornment={<IconStartAdornment name="AngleLeft" />}
            variant="ghost"
          >
            {t('reschedule.confirm.back')}
          </Button>
          <Heading level="h2" className="mb-40 md:mb-64">
            {t('reschedule.confirm.mainHeading')}
          </Heading>

          <AppointmentDate date={new Date(timeSlot)} />
        </div>
        <div className="flex flex-col sm:flex-row w-full xs:w-auto">
          <Button
            onClick={onSubmit}
            size="lg"
            className="mb-[16px] sm:mb-0 sm:mr-8"
            fullWidth={isMobile}
            loading={isRescheduling}
            disabled={isRescheduling}
            variant="primary"
          >
            {t('reschedule.confirm.submit')}
          </Button>
          <Button size="lg" onClick={() => navigate(-1)} variant="ghost">
            {t('reschedule.confirm.cancel')}
          </Button>
        </div>

        <RescheduleConfirmationModal modalProps={modalProps} status={modalStatus} onConfirm={onConfirm} />
      </Container>
    </>
  );
};

export default RescheduleConfirmation;
